import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import CreatableSelect from 'react-select/creatable';
import { Search, Plus, AlertTriangle, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import AddConsumableModal from './AddConsumableModal';
import ConsumableDetailsModal from './ConsumableDetailsModal';
import AdjustStockModal from './AdjustStockModal';
import BatchEditModal, { BatchForEdit } from './BatchEditModal';
import AddBatchModal from './AddBatchModal';
import LightboxGallery from '../../components/LightboxGallery';
import { getExpiryStatus } from './consumableHelpers';
import { orderedBatchPhotos } from './batchPhotos';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

// Font matches the rest of the app's tables (e.g. NursesPage) — no monospace override.
const PAGE_FONT = '"Inter", sans-serif';

// Palette shared with the "Refined Ledger" direction, kept consistent app-wide.
const C = {
  ink: '#0f1c2e', slate: '#5b6b7e', slateSoft: '#8695a6',
  paper: '#f6f8fb', line: '#dde5ee', lineStrong: '#c3cedb', cluster: '#a9bfdb', divider: '#93a8c6',
  accent: '#2657b3',
  warn: '#9a6b0f', warnSoft: '#fbf1de', warnBorder: '#f0dcae',
  crit: '#b0263a', critSoft: '#fbe9ea', critBorder: '#f0c6cb',
};

// Header blue matches the Nurses-list table convention — darker text + larger
// size than a normal row so the header reads clearly as its own band.
const HEADER_BLUE = '#86afd8';
const HEADER_TEXT = '#0c2138';

const globalCss = `
  .c3tbl { border-collapse: collapse; width: 100%; min-width: 1830px; table-layout: fixed; }
  /* light grid within every section */
  .c3tbl td, .c3tbl .cols th { border: 1px solid ${C.line}; }
  .c3tbl .grp th { background:${HEADER_BLUE}; color:${HEADER_TEXT}; font-size:0.7rem; font-weight:800; letter-spacing:0.1em; text-transform:uppercase; padding:0.6rem 0.8rem; text-align:center; border-bottom:1px solid #6f9cc9; white-space:nowrap; }
  .c3tbl .cols th { background:${HEADER_BLUE}; color:${HEADER_TEXT}; font-size:0.82rem; font-weight:800; letter-spacing:0.03em; text-align:center; padding:0.6rem 0.8rem 0.8rem; white-space:nowrap; }
  .c3tbl td { padding:0.7rem 0.9rem; vertical-align:middle; font-size:0.84rem; white-space:nowrap; text-align:center; color:${C.ink}; }
  /* darker lines segregating sections: vertical cluster dividers, header rule, group separators */
  .c3tbl .cl { border-left:2px solid ${C.divider}; }
  .c3tbl .grp th.cl, .c3tbl .cols th.cl { border-left:2px solid #4d739e; }
  .c3tbl .cols th { border-bottom:2px solid #375986; }
  .c3tbl tr.grp-first td { border-top:2px solid ${C.divider}; }
  .c3tbl .num { font-variant-numeric:tabular-nums; }
  .c3tbl .name-link { cursor:pointer; }
  .c3tbl .name-link:hover { text-decoration:underline; color:${C.accent}; }
`;

const fmtCurrency = (v?: number | null) =>
  v != null ? `₹${v.toFixed(2)}` : '–';

const fmtDate = (d?: string | null) => {
  if (!d) return '–';
  const [y, m, day] = d.split('T')[0].split('-');
  return `${day}-${m}-${y}`;
};

// ─── Types ───────────────────────────────────────────────────────────────────

interface ConsumableBatch {
  id: number;
  batch_no: string | null;
  vendor_name: string | null;
  hsn_sac: string | null;
  purchase_inv_no: string | null;
  purchase_inv_date: string | null;
  expiry_date: string | null;
  initial_quantity: number;
  current_quantity: number;
  status: string;
  mrp: number | null;
  dealer_price: number | null;
  selling_price: number | null;
  photo_1_url: string | null;
  photo_2_url: string | null;
  photo_3_url: string | null;
  photo_4_url: string | null;
  photo_5_url: string | null;
  primary_photo_slot: number | null;
}

interface Consumable {
  id: number;
  name: string;
  category: string | null;
  priority: number | null;
  gst_percent: number | null;
  low_stock_alert_quantity: number;
  is_active: boolean;
  total_stock: number;
  has_expired_batch: boolean;
  has_expiring_soon_batch: boolean;
  photo_1_url: string | null;
  photo_2_url: string | null;
  photo_3_url: string | null;
  photo_4_url: string | null;
  photo_5_url: string | null;
  batches: ConsumableBatch[];
}

const sortBatches = (batches: ConsumableBatch[]) =>
  [...batches].sort((a, b) => {
    if (!a.expiry_date && !b.expiry_date) return 0;
    if (!a.expiry_date) return 1;
    if (!b.expiry_date) return -1;
    return new Date(a.expiry_date).getTime() - new Date(b.expiry_date).getTime();
  });

// The batch whose details pre-fill "Add Stock" — the most recently purchased
// lot (falls back to the highest id, i.e. newest added, then any batch).
const latestBatch = (batches: ConsumableBatch[]): ConsumableBatch | null => {
  if (!batches.length) return null;
  return [...batches].sort((a, b) => {
    const da = a.purchase_inv_date ? new Date(a.purchase_inv_date).getTime() : 0;
    const db = b.purchase_inv_date ? new Date(b.purchase_inv_date).getTime() : 0;
    return db - da || b.id - a.id;
  })[0];
};

// Identity(5) + Dates(2) + Pricing(3) + Qty(1) — everything between the
// consumable's left block and its Actions column (used for the 0-batch row).
const BATCH_COL_SPAN = 11;

// ─── Pills ────────────────────────────────────────────────────────────────────

const pillBase: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '0.2rem',
  fontSize: '0.62rem', fontWeight: 700, padding: '0.12rem 0.4rem',
  borderRadius: '999px', whiteSpace: 'nowrap', lineHeight: 1.3,
};
const pillCrit: React.CSSProperties = { ...pillBase, background: C.critSoft, color: C.crit, border: `1px solid ${C.critBorder}` };
const pillWarn: React.CSSProperties = { ...pillBase, background: C.warnSoft, color: C.warn, border: `1px solid ${C.warnBorder}` };

const stackCell: React.CSSProperties = { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.25rem' };

// ─── Action buttons ────────────────────────────────────────────────────────────

const actBtnBase: React.CSSProperties = {
  width: '72px', padding: '4px 0', border: 'none', borderRadius: '5px',
  fontSize: '0.68rem', fontWeight: 700, color: '#fff', cursor: 'pointer',
  letterSpacing: '0.01em',
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────

const ConsumablesDashboard: React.FC = () => {
  const [consumables, setConsumables] = useState<Consumable[]>([]);
  const [isLoading, setIsLoading]     = useState(true);
  const [searchTerm, setSearchTerm]   = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Inline category editing
  const [editingCatId, setEditingCatId] = useState<number | null>(null);
  const [savingCat, setSavingCat]       = useState(false);

  // Inline priority editing
  const [editingPriId, setEditingPriId] = useState<number | null>(null);
  const [savingPri, setSavingPri]       = useState(false);

  const [isAddConsumableOpen, setIsAddConsumableOpen] = useState(false);
  const [drawerTarget, setDrawerTarget] = useState<Consumable | null>(null);
  const [adjustTarget, setAdjustTarget] = useState<ConsumableBatch | null>(null);
  const [editTarget, setEditTarget]     = useState<BatchForEdit | null>(null);
  const [addStockTarget, setAddStockTarget] = useState<Consumable | null>(null);

  const getHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
  });

  const fetchConsumables = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get<Consumable[]>(`${API_URL}/api/consumables/`, { headers: getHeaders() });
      setConsumables(res.data);
    } catch {
      toast.error('Failed to load consumables.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchConsumables(); }, [fetchConsumables]);

  const categoryOptions = Array.from(
    new Set(consumables.map(c => c.category).filter(Boolean) as string[])
  ).sort((a, b) => a.localeCompare(b));

  const saveCategory = async (consumableId: number, value: string) => {
    if (savingCat) return;
    setSavingCat(true);
    try {
      const fd = new FormData();
      fd.append('consumable_data', JSON.stringify({ category: value.trim() || null }));
      await axios.put(`${API_URL}/api/consumables/${consumableId}`, fd, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setEditingCatId(null);
      await fetchConsumables();
    } catch {
      toast.error('Failed to update category.');
    } finally {
      setSavingCat(false);
    }
  };

  const savePriority = async (consumableId: number, raw: string) => {
    if (savingPri) return;
    const trimmed = raw.trim();
    const parsed = trimmed === '' ? null : parseInt(trimmed, 10);
    if (parsed !== null && (isNaN(parsed) || parsed < 1)) {
      toast.error('Priority must be a whole number ≥ 1.');
      return;
    }
    setSavingPri(true);
    try {
      const fd = new FormData();
      fd.append('consumable_data', JSON.stringify({ priority: parsed }));
      await axios.put(`${API_URL}/api/consumables/${consumableId}`, fd, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setEditingPriId(null);
      await fetchConsumables();
    } catch {
      toast.error('Failed to update priority.');
    } finally {
      setSavingPri(false);
    }
  };

  // Order: category (A→Z, blank last) → priority (asc, blank last) → name (A→Z).
  const catRank = (c: Consumable) => (c.category ?? '').trim().toLowerCase();
  const sortConsumables = (list: Consumable[]) =>
    [...list].sort((a, b) => {
      const ca = catRank(a), cb = catRank(b);
      if (ca !== cb) {
        if (!ca) return 1;
        if (!cb) return -1;
        return ca.localeCompare(cb);
      }
      const pa = a.priority ?? Infinity, pb = b.priority ?? Infinity;
      if (pa !== pb) return pa - pb;
      return a.name.localeCompare(b.name);
    });

  const filtered = sortConsumables(consumables.filter(c => {
    if (categoryFilter && (c.category ?? '') !== categoryFilter) return false;
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    if (c.name.toLowerCase().includes(q)) return true;
    if ((c.category ?? '').toLowerCase().includes(q)) return true;
    return (c.batches ?? []).some(b =>
      (b.batch_no ?? '').toLowerCase().includes(q) ||
      (b.vendor_name ?? '').toLowerCase().includes(q)
    );
  }));

  const totalCols = 7 + BATCH_COL_SPAN + 1;

  return (
    <div style={{ fontFamily: PAGE_FONT }}>
      <style>{globalCss}</style>

      {/* ── Toolbar ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', border: `1px solid ${C.lineStrong}`, borderRadius: '0.5rem', padding: '0.45rem 0.8rem', background: '#fff', minWidth: 260 }}>
          <Search size={15} color={C.slateSoft} />
          <input
            placeholder="Search by name, batch no. or vendor…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '0.85rem', width: '100%', color: C.ink }}
          />
        </div>
        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          style={{ border: `1px solid ${C.lineStrong}`, borderRadius: '0.5rem', padding: '0.5rem 0.8rem', background: '#fff', fontSize: '0.85rem', color: C.ink, cursor: 'pointer', minWidth: 170 }}
        >
          <option value="">All Categories</option>
          {categoryOptions.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setIsAddConsumableOpen(true)}
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1.05rem', background: C.accent, color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
        >
          <Plus size={15} /> Add New Consumable
        </button>
      </div>

      {/* ── Merged Table ── */}
      <div style={{ background: '#fff', border: `1px solid ${C.line}`, borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 2px rgba(15,28,46,.04)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="c3tbl">
            <colgroup>
              <col style={{ width: '34px'  }} /><col style={{ width: '220px' }} /><col style={{ width: '130px' }} /><col style={{ width: '72px' }} /><col style={{ width: '52px' }} /><col style={{ width: '92px' }} /><col style={{ width: '70px' }} />
              <col style={{ width: '90px'  }} /><col style={{ width: '58px'  }} /><col style={{ width: '130px' }} /><col style={{ width: '90px' }} /><col style={{ width: '100px' }} />
              <col style={{ width: '100px' }} /><col style={{ width: '132px' }} />
              <col style={{ width: '84px'  }} /><col style={{ width: '84px'  }} /><col style={{ width: '84px'  }} />
              <col style={{ width: '112px' }} />
              <col style={{ width: '92px'  }} />
            </colgroup>
            <thead>
              <tr className="grp">
                <th colSpan={7}>Consumable</th>
                <th colSpan={5} className="cl">Identity</th>
                <th colSpan={2} className="cl">Dates</th>
                <th colSpan={3} className="cl">Pricing</th>
                <th colSpan={2} className="cl">Stock</th>
              </tr>
              <tr className="cols">
                <th>#</th>
                <th>Name</th>
                <th>Category</th>
                <th>Priority</th>
                <th>GST</th>
                <th>Low Alert</th>
                <th>Total</th>
                <th className="cl">Batch No.</th>
                <th>Photo</th>
                <th>Vendor</th>
                <th>HSN/SAC</th>
                <th>Invoice No.</th>
                <th className="cl">Purchased</th>
                <th>Expiry</th>
                <th className="cl">MRP</th>
                <th>Dealer</th>
                <th>Selling</th>
                <th className="cl">Qty (cur/init)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={totalCols} style={{ padding: '2.5rem', color: C.slateSoft }}>Loading consumables…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={totalCols} style={{ padding: '2.5rem', color: C.slateSoft }}>No consumables found.</td></tr>
              ) : filtered.map((c, ci) => {
                const isLow    = c.total_stock <= c.low_stock_alert_quantity;
                const groupBg  = ci % 2 === 0 ? '#ffffff' : C.paper;
                const batches  = sortBatches(c.batches ?? []);
                const span     = Math.max(1, batches.length);
                const cx: React.CSSProperties = { background: groupBg };

                const viewBtn = (
                  <button onClick={() => setDrawerTarget(c)} style={{ ...actBtnBase, background: 'linear-gradient(135deg,#6366f1,#4f46e5)' }}>View</button>
                );

                const addStockBtn = (
                  <button
                    onClick={() => setAddStockTarget(c)}
                    title="Add stock (new batch, pre-filled from the latest batch)"
                    style={{ ...actBtnBase, background: 'linear-gradient(135deg,#10b981,#059669)' }}
                  >
                    + Add Stock
                  </button>
                );

                // Consumable-level cells — rendered once, spanning all batch rows
                const consumableCells = (
                  <>
                    <td rowSpan={span} style={{ ...cx, color: C.slateSoft, fontWeight: 600 }}>{ci + 1}</td>
                    <td
                      rowSpan={span}
                      className="name-link"
                      onClick={() => setDrawerTarget(c)}
                      title="View details"
                      style={{ ...cx, fontWeight: 600, whiteSpace: 'normal', lineHeight: 1.3 }}
                    >
                      {c.name}
                    </td>
                    <td rowSpan={span} style={{ ...cx, whiteSpace: 'normal' }}>
                      {editingCatId === c.id ? (
                        <div style={{ minWidth: 130 }} onClick={e => e.stopPropagation()}>
                          <CreatableSelect
                            autoFocus
                            openMenuOnFocus
                            isClearable
                            isDisabled={savingCat}
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                            options={categoryOptions.map(cat => ({ value: cat, label: cat }))}
                            defaultValue={c.category ? { value: c.category, label: c.category } : null}
                            placeholder="Select or create…"
                            formatCreateLabel={(input: string) => `+ Create "${input}"`}
                            onChange={(opt: any) => saveCategory(c.id, opt ? opt.value : '')}
                            onCreateOption={(input: string) => saveCategory(c.id, input)}
                            onBlur={() => { if (!savingCat) setEditingCatId(null); }}
                            styles={{
                              menuPortal: base => ({ ...base, zIndex: 9999 }),
                              control: base => ({ ...base, minHeight: 32, fontSize: '0.8rem' }),
                              dropdownIndicator: base => ({ ...base, padding: 4 }),
                              clearIndicator: base => ({ ...base, padding: 4 }),
                            }}
                          />
                        </div>
                      ) : (
                        <span
                          className="name-link"
                          title="Click to set category"
                          onClick={() => setEditingCatId(c.id)}
                          style={{ display: 'inline-block', color: c.category ? C.ink : C.slateSoft, fontWeight: c.category ? 600 : 400, fontStyle: c.category ? 'normal' : 'italic' }}
                        >
                          {c.category || '— set —'}
                        </span>
                      )}
                    </td>
                    <td rowSpan={span} className="num" style={cx}>
                      {editingPriId === c.id ? (
                        <input
                          type="number"
                          min={1}
                          step={1}
                          autoFocus
                          disabled={savingPri}
                          defaultValue={c.priority ?? ''}
                          onClick={e => e.stopPropagation()}
                          onKeyDown={e => {
                            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                            else if (e.key === 'Escape') setEditingPriId(null);
                          }}
                          onBlur={e => savePriority(c.id, e.target.value)}
                          placeholder="#"
                          style={{ width: 52, padding: '2px 4px', border: `1px solid ${C.lineStrong}`, borderRadius: 4, fontSize: '0.8rem', textAlign: 'center' }}
                        />
                      ) : (
                        <span
                          className="name-link"
                          title="Click to set priority"
                          onClick={() => setEditingPriId(c.id)}
                          style={{ display: 'inline-block', color: c.priority != null ? C.ink : C.slateSoft, fontWeight: c.priority != null ? 600 : 400, fontStyle: c.priority != null ? 'normal' : 'italic' }}
                        >
                          {c.priority ?? '— set —'}
                        </span>
                      )}
                    </td>
                    <td rowSpan={span} className="num" style={cx}>{c.gst_percent != null ? `${c.gst_percent}%` : '–'}</td>
                    <td rowSpan={span} className="num" style={cx}>
                      <div style={stackCell}>
                        <span>{c.low_stock_alert_quantity}</span>
                        {isLow && (
                          <span style={pillCrit}><AlertTriangle size={10} /> Low stock</span>
                        )}
                      </div>
                    </td>
                    <td rowSpan={span} className="num" style={{ ...cx, color: isLow ? C.crit : C.ink }}>{c.total_stock}</td>
                  </>
                );

                if (batches.length === 0) {
                  return (
                    <tr key={c.id} className="grp-first">
                      {consumableCells}
                      <td colSpan={BATCH_COL_SPAN} className="cl" style={{ ...cx, color: C.slateSoft }}>
                        No batches recorded yet — click “Add Stock” to record the first batch.
                      </td>
                      <td style={cx}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                          {viewBtn}
                          {addStockBtn}
                        </div>
                      </td>
                    </tr>
                  );
                }

                return batches.map((b, bi) => {
                  const expStatus = getExpiryStatus(b.expiry_date);
                  const stripe    = expStatus === 'expired' ? C.crit : expStatus === 'soon' ? C.warn : C.divider;
                  const canAdjust = b.status === 'Active' && b.current_quantity > 0;
                  return (
                    <tr key={b.id} className={bi === 0 ? 'grp-first' : undefined}>
                      {bi === 0 && consumableCells}
                      <td className="cl num" style={{ borderLeft: `2px solid ${stripe}` }}>{b.batch_no || '–'}</td>
                      <td>
                        <LightboxGallery
                          images={orderedBatchPhotos(b).map(p => p.url)}
                          thumbnailSize={42}
                          alt={`Batch ${b.batch_no || b.id}`}
                          caption={`Batch ${b.batch_no || b.id}`}
                        />
                      </td>
                      <td style={{ whiteSpace: 'normal' }}>{b.vendor_name || '–'}</td>
                      <td>{b.hsn_sac || '–'}</td>
                      <td>{b.purchase_inv_no || '–'}</td>
                      <td className="cl">{fmtDate(b.purchase_inv_date)}</td>
                      <td>
                        <div style={stackCell}>
                          <span
                            className="num"
                            style={expStatus ? { color: expStatus === 'expired' ? C.crit : C.warn } : undefined}
                          >
                            {fmtDate(b.expiry_date)}
                          </span>
                          {expStatus === 'expired' && <span style={pillCrit}><AlertTriangle size={10} /> Expired</span>}
                          {expStatus === 'soon'    && <span style={pillWarn}><Clock size={10} /> Exp. soon</span>}
                        </div>
                      </td>
                      <td className="cl num">{fmtCurrency(b.mrp)}</td>
                      <td className="num">{fmtCurrency(b.dealer_price)}</td>
                      <td className="num" style={{ fontWeight: 700, color: C.accent }}>{fmtCurrency(b.selling_price)}</td>
                      <td className="cl num">
                        <span>{b.current_quantity}</span>
                        <span> / {b.initial_quantity}</span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                          {bi === 0 && viewBtn}
                          {bi === 0 && addStockBtn}
                          <button
                            onClick={() => setEditTarget(b)}
                            title="Edit batch metadata"
                            style={{ ...actBtnBase, background: 'linear-gradient(135deg,#0ea5e9,#2563eb)' }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => setAdjustTarget(b)}
                            disabled={!canAdjust}
                            title={!canAdjust ? (b.current_quantity === 0 ? 'Batch is empty' : `Cannot adjust a ${b.status} batch`) : 'Adjust stock'}
                            style={{
                              ...actBtnBase,
                              background: canAdjust ? 'linear-gradient(135deg,#f43f5e,#e11d48)' : '#dbe2ea',
                              color: canAdjust ? '#fff' : '#9aa7b6',
                              cursor: canAdjust ? 'pointer' : 'not-allowed',
                            }}
                          >
                            Adjust
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                });
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Modals ── */}
      {isAddConsumableOpen && (
        <AddConsumableModal
          onClose={() => setIsAddConsumableOpen(false)}
          onSuccess={() => { setIsAddConsumableOpen(false); fetchConsumables(); }}
          existingConsumables={consumables}
        />
      )}
      {adjustTarget && (
        <AdjustStockModal
          batchId={adjustTarget.id}
          currentQuantity={adjustTarget.current_quantity}
          batchNo={adjustTarget.batch_no}
          onClose={() => setAdjustTarget(null)}
          onSuccess={() => { setAdjustTarget(null); fetchConsumables(); }}
        />
      )}
      {editTarget && (
        <BatchEditModal
          batch={editTarget}
          onClose={() => setEditTarget(null)}
          onSuccess={() => { setEditTarget(null); fetchConsumables(); }}
        />
      )}
      {addStockTarget && (
        <AddBatchModal
          consumable={{ id: addStockTarget.id, name: addStockTarget.name }}
          prefill={latestBatch(addStockTarget.batches ?? [])}
          onClose={() => setAddStockTarget(null)}
          onSuccess={() => { setAddStockTarget(null); fetchConsumables(); }}
        />
      )}

      {/* ── Details Modal (catalog info + adjustment history + delete) ── */}
      {drawerTarget && (
        <ConsumableDetailsModal
          consumable={drawerTarget}
          onClose={() => setDrawerTarget(null)}
          onDataChange={fetchConsumables}
          onDeleted={() => { setDrawerTarget(null); fetchConsumables(); }}
        />
      )}
    </div>
  );
};

export default ConsumablesDashboard;
